import { Controller } from "@hotwired/stimulus"
import { cable } from "../channels/cable"
import * as Turbo from "@hotwired/turbo"
import { getMicIntent, getCamIntent, setMicIntent, setCamIntent, subscribe, resetMediaIntent } from "../components/lib/video_media_intent"

export default class extends Controller {
  static targets = [
    "idleState", "incomingState", "activeState", "activeVideo",
    "mobileIncoming", "mobileActive", "mobileControls", "standaloneDock",
    "mobileMicBtn", "mobileCamBtn", "panelFrame"
  ]

  static values = {
    state: { type: String, default: "idle" },
    studyClassId: Number,
    createUrl: String,
    tokenUrl: String,
    videoSessionId: Number,
    destroyUrl: String,
    role: String,
    currentMemberId: Number,
    labels: String,
    startedAt: { type: String, default: "" },
  }

  connect() {
    // CLA-80 bugfix: Stimulus's ValueObserver invokes `stateValueChanged`
    // synchronously as part of binding the initial DOM attribute -- BEFORE
    // this connect() method runs -- passing the *default* value ("idle") as
    // `previousValue` (not `undefined`) whenever the attribute is already
    // present in the DOM, which it always is here (server-rendered). Without
    // this flag, stateValueChanged's own transition guard can't tell that
    // early, pre-connect call apart from a genuine later transition, so it
    // would mark the session as auto-opened before this connect()'s own
    // (correctly-timed, deferred) dispatch below gets a chance to run.
    this._hasConnected = true

    // CLA-82 review: recovery boundary for the set-before-fetch/clear-in-
    // finally guard in startCall()/joinCall() -- a hung (never-settling)
    // fetch would otherwise leave `_callActionInFlight` stuck `true` for the
    // page's lifetime, killing both call surfaces. The permanent shell
    // detaches/reattaches on every in-class Turbo visit, so resetting it
    // here gives a real unstick path without changing that guard's shape.
    this._callActionInFlight = false

    // Cancel a pending teardown scheduled by a same-task disconnect(): the
    // turbo-permanent shell detaches/reattaches this node within the same
    // task on an in-class Turbo visit, so if connect() runs before the
    // deferred teardown fires, the subscription never actually needs to drop.
    if (this.pendingTeardown) {
      clearTimeout(this.pendingTeardown)
      this.pendingTeardown = null
    }

    // 3.0: Instrumentation counter for tests — incremented once per real connect().
    window.__videoCallConnectCount = (window.__videoCallConnectCount || 0) + 1

    // Subscribe only if not already subscribed (guard for hypothetical re-use,
    // though the permanent node means this fires exactly once per page load).
    if (!this.channel) {
      this.subscribeToChannel()
    }

    this.boundHandleCallEnded = this.handleCallEnded.bind(this)
    document.addEventListener("video-call:ended", this.boundHandleCallEnded)

    this.boundHandleEnter = this.handleEnter.bind(this)
    document.addEventListener("video-call:enter", this.boundHandleEnter)

    // 3.3: turbo:before-visit handler — warn when navigating away during an active call.
    this.boundHandleBeforeVisit = this.handleBeforeVisit.bind(this)
    document.addEventListener("turbo:before-visit", this.boundHandleBeforeVisit)

    // CLA-81 review should-fix 3: keep the mobile mic/cam pill's colour in
    // sync with the shared intent module -- an initial sync (it may already
    // be non-default on an in-class Turbo reconnect) plus a live subscribe
    // so a toggle from the React ControlBar (in the dock of the open
    // chat panel) updates this pill too, instead of leaving it a stale,
    // unreconciled third copy.
    this._syncMobileMicClasses(getMicIntent())
    this._syncMobileCamClasses(getCamIntent())
    this.unsubscribeMediaIntent = subscribe(({ micEnabled, camEnabled }) => {
      this._syncMobileMicClasses(micEnabled)
      this._syncMobileCamClasses(camEnabled)
    })

    // CLA-81 review C6/C7: observe-only (this controller writes `.video-pip`
    // itself but only ever reads `.hidden`, which belongs to chat_controller.js
    // -- see _setUpPipObserver()'s docstring for why a MutationObserver is
    // the only way to see the composite transition from here).
    this._setUpPipObserver()

    this.renderState()

    // 5.1 (D6/case b): arrive-mid-call — deferred so it fires after
    // chat_controller's own connect() has attached its "chat:open" listener
    // (video root is the ancestor, so this connect() runs first — see A1).
    // CLA-80 bugfix: gated on _alreadyAutoOpened() so this doesn't re-fire on
    // every in-class Turbo visit — the turbo-permanent shell detaches/
    // reattaches this node on every same-class Turbo visit, so `stateValue`
    // is still "incoming"/"active" for the same session on every reconnect
    // even after the user closed the panel. See _alreadyAutoOpened()'s
    // docstring for why the marker lives on the element's dataset rather
    // than as controller-instance state.
    if (this.stateValue === "incoming" || this.stateValue === "active") {
      queueMicrotask(() => {
        if (this._alreadyAutoOpened()) return
        // Mark only if the dispatch actually happened (desktop viewport) --
        // see _dispatchChatOpen()'s docstring.
        if (this._dispatchChatOpen()) this._markAutoOpened()
      })
    }
  }

  disconnect() {
    document.removeEventListener("video-call:ended", this.boundHandleCallEnded)
    document.removeEventListener("video-call:enter", this.boundHandleEnter)
    document.removeEventListener("turbo:before-visit", this.boundHandleBeforeVisit)

    if (this.unsubscribeMediaIntent) {
      this.unsubscribeMediaIntent()
      this.unsubscribeMediaIntent = null
    }

    // Unlike the cable teardown below, safe to disconnect synchronously (not
    // deferred): a MutationObserver has no external subscription to lose, so
    // a same-class Turbo reconnect just recreates an equivalent one in the
    // next connect() at no cost.
    if (this._pipObserver) {
      this._pipObserver.disconnect()
      this._pipObserver = null
    }

    // Defer the actual cable teardown by one task rather than tearing down
    // synchronously: the turbo-permanent shell detaches/reattaches this node
    // within the same task on an in-class Turbo visit, so connect() above
    // cancels this before it runs and the subscription is never actually
    // dropped. A genuine unmount (leaving the class) has no matching
    // connect() to cancel it, so the teardown still fires — the null-out
    // below is the fallback for that path, as before.
    this.pendingTeardown = setTimeout(() => {
      this.pendingTeardown = null
      if (this.unsubMessage) this.unsubMessage()
      if (this.channel) this.channel.disconnect()
      this.unsubMessage = null
      this.channel = null
    }, 0)

    // CLA-80 bugfix: reset synchronously (not deferred like the teardown
    // above) so `_hasConnected` never depends on a Stimulus internal. Today a
    // pre-connect `stateValueChanged` genuinely can't fire on a reconnect
    // (`StringMapObserver#stop` doesn't clear its cached string map, so there
    // is no "attribute added" event to replay) -- but if that internal ever
    // changes, a sticky `true` here would make stateValueChanged treat the
    // replayed pre-connect call as a real transition and dispatch without
    // consulting the marker, one task before connect() sets it back to true.
    this._hasConnected = false
  }

  // --- Actions ---

  async startCall() {
    // CLA-82 (D7): guard shared with joinCall() -- a double-click gives the
    // backend's active-session uniqueness guard two token requests for the
    // same LiveKit identity, so it returns two tokens for one identity and
    // LiveKit evicts the first connection (a visible disconnect flash and a
    // possibly stranded mic track), not a leaked Room. One boolean protects
    // both surfaces (header and dock) that can now reach these methods.
    if (this._callActionInFlight) return
    this._callActionInFlight = true
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) throw new Error("Failed to start call")

      const data = await response.json()
      this.videoSessionIdValue = data.video_session_id
      // CLA-80 bugfix: mark this session's auto-open budget as spent here.
      // startCall() only ever runs from a click on a control INSIDE the
      // already-open panel (the dock lives inside the chat panel), OR from
      // handleEnter(), which is only reached after a `chat:open` has already
      // been dispatched in the same task -- so a chat panel, if present, is
      // open either way, which is the property this marker depends on.
      // Without this, a later in-class Turbo reconnect (state still "active",
      // panel already closed by the user) would see no marker
      // (stateValueChanged only marks on a transition INTO "incoming", which
      // this session never has) and reopen the panel the user just closed.
      this._markAutoOpened()
      this.mountVideoPanel(data.token, data.server_url, data.started_at)
      // CLA-82/D12: write before stateValue = "active" -- the dispatch in
      // stateValueChanged fires from that assignment, so this ordering
      // guarantees no listener can observe "active" with an empty start time.
      this.startedAtValue = data.started_at || ""
      this.stateValue = "active"
    } catch (error) {
      console.error("Failed to start video call:", error)
    } finally {
      this._callActionInFlight = false
    }
  }

  async joinCall() {
    // CLA-82 (D7): see startCall()'s comment -- same guard, shared boolean.
    if (this._callActionInFlight) return
    this._callActionInFlight = true
    try {
      const tokenUrl = this.tokenUrlValue.replace("__SESSION_ID__", this.videoSessionIdValue)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(tokenUrl, {
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
      })

      if (!response.ok) throw new Error("Failed to join call")

      const data = await response.json()
      // CLA-80 bugfix: same reasoning as startCall() -- joining is a click on
      // a control inside the already-open panel, so this session's auto-open
      // budget is spent. `videoSessionIdValue` is already set to the session
      // being joined (from the incoming broadcast or initial render), so
      // this marks the right session.
      this._markAutoOpened()
      this.mountVideoPanel(data.token, data.server_url, data.started_at)
      // CLA-82/D12: write before stateValue = "active" -- see startCall().
      this.startedAtValue = data.started_at || ""
      this.stateValue = "active"
    } catch (error) {
      console.error("Failed to join video call:", error)
    } finally {
      this._callActionInFlight = false
    }
  }

  // CLA-81 review A3: writes the shared intent module rather than deriving
  // from `participant.isMicrophoneEnabled` -- the React ControlBar reads/
  // writes the same module, so the two surfaces can no longer disagree.
  // The mobile pill's own colour isn't set here -- it's driven by the
  // `subscribe()` callback wired up in connect() (should-fix 3), which
  // fires for this change too, so a manual write here would be redundant.
  toggleMic() {
    const room = window.__livekitRoom
    if (!room?.localParticipant) return

    const participant = room.localParticipant
    const enabled = getMicIntent()
    setMicIntent(!enabled)
    participant.setMicrophoneEnabled(!enabled)
  }

  toggleCam() {
    const room = window.__livekitRoom
    if (!room?.localParticipant) return

    const participant = room.localParticipant
    const enabled = getCamIntent()
    setCamIntent(!enabled)
    participant.setCameraEnabled(!enabled)
  }

  // CLA-81 review should-fix 3: single source of truth for the mobile pill's
  // colour, called both for the initial sync in connect() and from the
  // media-intent subscribe callback -- so a toggle from either surface
  // (this mobile button or the React ControlBar) ends up applying the same
  // classes the same way, rather than each surface writing them separately.
  _syncMobileMicClasses(micEnabled) {
    if (this.hasMobileMicBtnTarget) {
      this.mobileMicBtnTarget.classList.toggle("text-error", !micEnabled)
      this.mobileMicBtnTarget.classList.toggle("text-content-muted", micEnabled)
    }
  }

  _syncMobileCamClasses(camEnabled) {
    if (this.hasMobileCamBtnTarget) {
      this.mobileCamBtnTarget.classList.toggle("text-error", !camEnabled)
      this.mobileCamBtnTarget.classList.toggle("text-content-muted", camEnabled)
    }
  }

  // --- State Management ---

  stateValueChanged(value, previousValue) {
    this.renderState()

    // Task 6.1/D1: symmetric twin of chat_controller.js's `chat:state {open}`
    // broadcast -- dispatched unconditionally (not gated on `_hasConnected`
    // like the auto-open side effect below), including the documented
    // pre-connect invocation, because a state broadcast is idempotent and
    // gating it would create a window where a real state is never
    // broadcast. Carries `startedAt` alongside `state` so the header's
    // handler stays a pure function of the payload, with no DOM re-query
    // from inside this value-changed callback.
    document.dispatchEvent(new CustomEvent("video-call:state", {
      detail: { state: value, startedAt: this.startedAtValue || null },
    }))

    // 5.1 (D6/case a): genuine transition to incoming — guard out the initial
    // connect assignment. `this._hasConnected` is required in addition to the
    // `previousValue !== undefined` check: Stimulus's ValueObserver invokes
    // this callback synchronously BEFORE connect() runs whenever the DOM
    // attribute is already present (server-rendered, always true here),
    // passing the *default* value ("idle") as `previousValue` rather than
    // `undefined` — so the `undefined` check alone doesn't catch it. Without
    // `_hasConnected`, that pre-connect call would mark the session as
    // auto-opened before connect()'s own deferred dispatch (case b) below
    // gets a chance to run, silently swallowing it.
    // Unlike connect()'s auto-open, this fires unconditionally on a genuine
    // transition (a NEW incoming call) rather than checking
    // _alreadyAutoOpened() first — it still records the marker afterwards so
    // a later same-session reconnect (see connect()) does not re-fire.
    if (this._hasConnected && previousValue !== undefined && value === "incoming" && previousValue !== "incoming") {
      // Mark only if the dispatch actually happened (desktop viewport) --
      // see _dispatchChatOpen()'s docstring.
      if (this._dispatchChatOpen()) this._markAutoOpened()
    }
  }

  // 5.1 (D6): desktop-only auto-open of the chat panel on an incoming call.
  // Returns whether it actually dispatched, so callers only mark the session
  // as auto-opened when something really opened (CLA-80 bugfix) -- on a <lg
  // viewport this is a no-op, and marking anyway would permanently swallow
  // that session's auto-open once the viewport widens later.
  _dispatchChatOpen() {
    if (window.matchMedia("(width >= 64rem)").matches) {
      document.dispatchEvent(new CustomEvent("chat:open"))
      return true
    }
    return false
  }

  // CLA-80 bugfix: per-session "already auto-opened"/"spent" marker. Stored
  // on `this.element`'s dataset (the turbo-permanent-relocated node) rather
  // than as controller-instance state. This file's own `pendingTeardown`
  // mechanism only works because Stimulus actually reuses the SAME
  // controller instance across a same-class Turbo reconnect (it caches
  // Scope→Context per element+identifier -- see the test
  // "widget's VideoCallChannel subscription survives a same-class Turbo
  // navigation without dropping" in test/system/video_call_widget_test.rb),
  // so instance state would in practice survive too. The dataset is still
  // the more defensive choice: it doesn't depend on that instance-reuse
  // behavior remaining true, and it is also the only option for
  // startCall()/joinCall() marking a session that _dispatchChatOpen() itself
  // never ran for (see those methods).
  _alreadyAutoOpened() {
    return this.element.dataset.chatAutoOpenedSession === String(this.videoSessionIdValue)
  }

  _markAutoOpened() {
    this.element.dataset.chatAutoOpenedSession = String(this.videoSessionIdValue)
  }

  renderState() {
    const state = this.stateValue

    // Desktop states
    if (this.hasIdleStateTarget) {
      this.idleStateTarget.classList.toggle("hidden", state !== "idle")
    }
    if (this.hasIncomingStateTarget) {
      this.incomingStateTarget.classList.toggle("hidden", state !== "incoming")
    }
    if (this.hasActiveStateTarget) {
      this.activeStateTarget.classList.toggle("hidden", state !== "active")
    }

    // Mobile: incoming banner (with text + pulse)
    if (this.hasMobileIncomingTarget) {
      this.mobileIncomingTarget.classList.toggle("hidden", state !== "incoming")
    }

    // Mobile: active controls (compact icon + mic/cam)
    if (this.hasMobileActiveTarget) {
      this.mobileActiveTarget.classList.toggle("hidden", state !== "active")
    }

    if (this.hasStandaloneDockTarget) {
      this.standaloneDockTarget.classList.toggle("max-lg:hidden", state === "idle")
    }

    // CLA-81: PiP presentation state of the chat panel frame -- active-only,
    // no viewport guard (desktop-only is enforced entirely by the
    // `@media (width >= 64rem)` wrapper around the `.video-pip` CSS).
    if (this.hasPanelFrameTarget) this.panelFrameTarget.classList.toggle("video-pip", state === "active")
  }

  // --- PiP accessible name + focus handoff (CLA-81 review C6/C7) ---
  //
  // The frame is genuinely presenting as the PiP card only when it carries
  // BOTH `.video-pip` (this controller's own, toggled in renderState()) AND
  // `.hidden` (chat_controller.js's) -- the two are written by different
  // controllers, so a MutationObserver on the frame's `class` attribute is
  // the only way to see that composite transition from here. This mechanism
  // only ever reads the attribute; it writes neither class itself, so the
  // one-writer-per-class split stays intact.

  _setUpPipObserver() {
    if (!this.hasPanelFrameTarget) return

    // Reflects whatever is already on the frame (e.g. a same-class Turbo
    // reconnect mid-PiP) without treating that as a fresh transition --
    // only a later, genuine flip triggers the aria/focus handling below.
    this._wasPip = this._isPip()
    this._applyPipAccessibleName(this._wasPip)

    // Guards a hypothetical double setup the same way connect()'s own
    // `if (!this.channel)` does for the cable subscription -- unreachable
    // today given Stimulus's connect() ordering, but cheap to keep aligned.
    this._pipObserver?.disconnect()
    this._pipObserver = new MutationObserver(() => {
      const isPip = this._isPip()
      if (isPip === this._wasPip) return
      this._wasPip = isPip
      this._applyPipAccessibleName(isPip)
      this._handlePipFocusHandoff(isPip)
    })
    this._pipObserver.observe(this.panelFrameTarget, { attributes: true, attributeFilter: ["class"] })
  }

  _isPip() {
    return this.hasPanelFrameTarget &&
      this.panelFrameTarget.classList.contains("video-pip") &&
      this.panelFrameTarget.classList.contains("hidden")
  }

  // C6: `aria-label` alone is dropped from the accessibility tree on a
  // `generic`-role (bare `<div>`) element, so the frame needs an explicit
  // `role` to carry a name at all. Applied only while `_isPip()` is true --
  // the same frame is an ordinary docked chat panel the rest of the time,
  // and must not carry "floating video call window" as its name then.
  _applyPipAccessibleName(isPip) {
    const el = this.panelFrameTarget
    const labels = this.labelsValue ? JSON.parse(this.labelsValue) : {}
    // A blank label would still set `role="region"`, producing a nameless
    // landmark region -- which assistive tech drops from the landmark list
    // entirely, arguably worse than the bare `generic` <div> this replaces.
    // Skip both attributes rather than ship that.
    if (isPip && labels.pipAriaLabel) {
      el.setAttribute("role", "region")
      el.setAttribute("aria-label", labels.pipAriaLabel)
    } else {
      el.removeAttribute("role")
      el.removeAttribute("aria-label")
    }
  }

  // C7: both PiP transitions remove the control the user just activated from
  // the rendering tree (the header ✕ on entry, the restore/hangup button on
  // exit), which resets document.activeElement to <body> -- hand focus to
  // the transition's natural landing spot instead. Only when focus was
  // actually lost by the transition (a user focused entirely elsewhere, e.g.
  // typing in #main-content, must not be interrupted), and only at the same
  // `lg` viewport PiP itself is gated on: below `lg` no PiP card exists, and
  // Safari/Firefox don't focus a tapped <button> at all -- without this
  // gate, tapping the bottom-nav chat button on a phone would read as
  // "focus lost" and pop the virtual keyboard open over the feed the user
  // just asked to see.
  _handlePipFocusHandoff(isPip) {
    if (!window.matchMedia("(width >= 64rem)").matches) return

    const active = document.activeElement
    const focusLost = !active || active === document.body || this.panelFrameTarget.contains(active)
    if (!focusLost) return

    const candidates = isPip
      ? [this.panelFrameTarget.querySelector("button.video-pip-only")]
      : [
          // Leaving PiP is ambiguous from `isPip` alone: the chat reopened
          // (this composer is genuinely rendered) or the call ended while
          // the card was showing (the frame keeps `.hidden`, so the
          // composer sits in a display:none subtree and `.focus()` on it
          // silently does nothing) -- `_focusFirstRendered` below falls
          // through to the navbar/bottom-nav chat button, the nearest
          // control that IS actually rendered in that second case.
          this.panelFrameTarget.querySelector("[data-chat-composer-target='textarea']"),
          ...document.querySelectorAll('[data-controller~="chat-button"]'),
        ]
    this._focusFirstRendered(candidates)
  }

  // Picks the first candidate that is actually rendered (`offsetParent` is
  // null for any element inside a `display: none` subtree) rather than
  // trusting `isPip` alone to predict which one will be -- `.focus()` on a
  // non-rendered element is a silent no-op, so an unrendered first choice
  // would otherwise leave focus stuck on <body>.
  _focusFirstRendered(candidates) {
    candidates.find((el) => el && el.offsetParent !== null)?.focus()
  }

  // --- ActionCable ---

  subscribeToChannel() {
    this.channel = cable.subscribeTo("VideoCallChannel", {
      study_class_id: this.studyClassIdValue,
    })

    this.unsubMessage = this.channel.on("message", (data) => {
      this.handleBroadcast(data)
    })

    this.channel.ensureSubscribed()

    // 3.0: Instrumentation counter for tests — incremented once per real subscribe.
    window.__videoCallSubscribeCount = (window.__videoCallSubscribeCount || 0) + 1
  }

  handleBroadcast(data) {
    if (data.type === "call_started" && data.member_id !== this.currentMemberIdValue) {
      // CLA-81 review B5: mirrors the `call_ended` branch's defensive shape
      // below. A `call_started` for the session we are ALREADY active in
      // must not disturb the live call at all. One for a genuinely
      // different session while still "active" means this client missed
      // the matching `call_ended` (e.g. an AnyCable blip) and is sitting on
      // a stale Room -- tear it down (unmount the panel, disconnect the
      // stale Room) BEFORE transitioning to "incoming". The Room reference
      // is read BEFORE unmountVideoPanel() rather than after: unmounting
      // only empties the DOM synchronously -- the actual React root
      // teardown (turbo-mount's disconnect() -> root.unmount() ->
      // RoomExposer's cleanup, which nulls window.__livekitRoom) runs at
      // the microtask checkpoint at the end of THIS task, not inline, so
      // reading the global after unmounting would still work today but
      // would make correctness depend on that ordering. What actually
      // rules out two overlapping Rooms: the only mount sites (startCall(),
      // joinCall()) are reached from user-initiated actions -- a click
      // handler, or handleEnter() via openPanel() / the `video-call:enter`
      // document event -- i.e. a later task -- by then the microtask checkpoint
      // has already run root.unmount(). Setting stateValue = "incoming"
      // here only drives renderState(), which toggles classes and mounts
      // nothing.
      if (this.stateValue === "active") {
        if (data.video_session_id === this.videoSessionIdValue) return

        const staleRoom = window.__livekitRoom
        this.unmountVideoPanel()
        if (staleRoom) staleRoom.disconnect().catch(() => {})
        // CLA-81 review blocker 2 / B5 follow-up: this is a genuine call
        // end for this client (the matching call_ended broadcast was
        // missed) -- resetMediaIntent()'s contract requires this on every
        // genuine-end site, same as handleCallEnded() and the call_ended
        // branch below, so a mute from the stale call doesn't silently
        // carry over into the new one.
        resetMediaIntent()
      }

      this.videoSessionIdValue = data.video_session_id
      // Task 6.8: genuine-end site -- this client's own start time no longer
      // applies to the (different, stale-revealed) session it is now
      // "incoming" for.
      this.startedAtValue = ""
      this.stateValue = "incoming"
      this.playNotificationSound()
    } else if (data.type === "call_ended") {
      if (this.stateValue === "idle") return
      if (data.video_session_id && data.video_session_id !== this.videoSessionIdValue) return
      // Task 6.8: genuine-end site.
      this.startedAtValue = ""
      this.stateValue = "idle"
      this.unmountVideoPanel()
      // CLA-81 review blocker 2: the call is genuinely over -- see
      // resetMediaIntent()'s docstring for why this scopes the intent to
      // the call rather than the page.
      resetMediaIntent()
    }
  }

  // --- Video Panel Mount/Unmount ---

  // Always mounts into the dock container (the unified panel's dock on every
  // viewport).
  mountVideoPanel(token, serverUrl, startedAt) {
    const container = this.activeVideoTarget
    container.innerHTML = ""

    const labels = this.labelsValue ? JSON.parse(this.labelsValue) : {}

    const wrapper = document.createElement("div")
    wrapper.setAttribute("data-controller", "turbo-mount")
    wrapper.setAttribute("data-turbo-mount-component-value", "VideoPanel")
    wrapper.setAttribute("data-turbo-mount-props-value", JSON.stringify({
      token,
      serverUrl,
      role: this.roleValue,
      labels,
      startedAt,
    }))
    container.appendChild(wrapper)
  }

  unmountVideoPanel() {
    if (this.hasActiveVideoTarget) {
      this.activeVideoTarget.innerHTML = ""
    }
  }

  // --- Event Handlers ---

  handleCallEnded() {
    this.unmountVideoPanel()
    // CLA-81 review blocker 2: this user's participation in the call has
    // genuinely ended (hangup), for both roles -- see resetMediaIntent()'s
    // docstring for why this scopes the intent to the call rather than the
    // page.
    resetMediaIntent()
    // CLA-82: genuine-end site, both branches.
    this.startedAtValue = ""
    if (this.roleValue === "teacher") {
      this.stateValue = "idle"
      this.endSessionOnServer()
    } else {
      this.stateValue = "incoming"
    }
  }

  // Header call button's "enter" intent (task 6.3/D1): the header dispatches
  // `video-call:enter` rather than commanding a specific action, so this
  // resolves it against `stateValue` --
  // "incoming" -> joinCall(), "idle" -> startCall(). "active" is a no-op: the
  // header's click already dispatched `chat:open` before this fires (D2), so
  // the panel is already revealed and there is nothing left to do here.
  handleEnter() {
    if (this.stateValue === "idle") {
      // Defensive only -- the server's 403 on a non-teacher POST is the real
      // gate; this just avoids firing a request doomed to fail.
      if (this.roleValue !== "teacher") return
      this.startCall()
    } else if (this.stateValue === "incoming") {
      this.joinCall()
    }
  }

  // The mobile pill's buttons. Opens the unified panel, then joins
  // when a call is incoming (no-op when active). Deliberately not
  // _dispatchChatOpen(): that is the desktop-gated auto-open path paired with
  // _markAutoOpened().
  openPanel() {
    document.dispatchEvent(new CustomEvent("chat:open"))
    this.handleEnter()
  }

  async endSessionOnServer() {
    if (!this.videoSessionIdValue) return

    try {
      const destroyUrl = this.destroyUrlValue.replace("__SESSION_ID__", this.videoSessionIdValue)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch(destroyUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
      })
    } catch (e) {
      // Session may already be ended — ignore
    }
  }

  // --- 3.3: Out-of-class navigation guard ---

  // Extract the study_class_id from a URL.
  // Matches:
  //   /classroom/study_classes/:id/...
  //   /study_classes/:id (covers /student/study_classes/:id, /teacher/study_classes/:id)
  // NOTE: no leading ^ anchor so role-prefixed variants like /student/study_classes/:id match.
  _studyClassIdFromUrl(url) {
    const classroomMatch = url.match(/\/classroom\/study_classes\/(\d+)/)
    if (classroomMatch) return parseInt(classroomMatch[1], 10)

    const bareMatch = url.match(/\/study_classes\/(\d+)/)
    if (bareMatch) return parseInt(bareMatch[1], 10)

    return null
  }

  handleBeforeVisit(event) {
    if (this.stateValue !== "active") return

    const targetUrl = event.detail.url
    const targetClassId = this._studyClassIdFromUrl(targetUrl)

    // Same class — let navigation proceed silently.
    if (targetClassId === this.studyClassIdValue) return

    // Out-of-class (different id or no class id in URL) — warn.
    event.preventDefault()

    const labels = this.labelsValue ? JSON.parse(this.labelsValue) : {}
    const warningText = labels.leaveWarning || "You are in an active video call. Leave the page?"

    if (window.confirm(warningText)) {
      // Tear down the call before navigating.
      // Set state to idle FIRST so the re-fired turbo:before-visit will not
      // preventDefault again (no infinite loop).
      // CLA-82: genuine-end site like the other two below.
      this.startedAtValue = ""
      this.stateValue = "idle"
      this.unmountVideoPanel()

      if (this.roleValue === "teacher") {
        // Teacher/owner ends the session for everyone.
        this.endSessionOnServer()
      }
      // For both roles, disconnect from the LiveKit room if present.
      const room = window.__livekitRoom
      if (room) room.disconnect()

      // CLA-81 review blocker 2: a genuine-end site like the other two
      // (handleCallEnded / the call_ended broadcast) -- this user's
      // participation is definitively over, not a remount/reparent.
      resetMediaIntent()

      Turbo.visit(targetUrl)
    }
    // On cancel: event already preventDefault'd — navigation stays aborted.
  }

  // --- Notification Sound ---

  async playNotificationSound() {
    let ctx
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === "suspended") {
        await ctx.resume()
      }
      const playTone = (time) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = 880
        gain.gain.value = 0.2
        osc.start(time)
        osc.stop(time + 0.15)
      }
      playTone(ctx.currentTime)
      playTone(ctx.currentTime + 0.25)
    } catch (e) {
      // AudioContext may not be available or blocked by browser policy
    } finally {
      if (ctx) setTimeout(() => ctx.close(), 500)
    }
  }
}
